import { useEffect, useState } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { UserClient, UserModel } from '../types';
import { createClient } from "../api/clients";
import UserTable from '../components/userTable';


function TeamManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  const [users, setUsers] = useState<UserModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const [userToDelete, setUserToDelete] = useState<UserModel | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const userClient = createClient(UserClient);

  useEffect(() => {
    const state = location.state as { message?: string } | null;

    if (state?.message && state.message.trim() !== "") {
      setInfoMessage(state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const users = await userClient.getUsers();
      setUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = () => {
    navigate('/team-management/create');
  };

  const handleEdit = (id?: string) => {
    if (!id) {
      return;
    }
    navigate(`/team-management/edit/${id}`, {
        state: { returnTo: '/team-management' }
    });
  };

  const handleDeleteClick = (user: UserModel) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
    setErrorMessage(null);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete?.id) {
      return;
    }

    try {
      const result = await userClient.deleteUser(userToDelete.id);

      if (result.success === false) {
        setErrorMessage(result.error ?? 'An error occurred while deleting the user. Please try again.');
        return;
      }

      await fetchUsers();
      setInfoMessage('User deleted successfully.');
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrorMessage('An error occurred while deleting the user. Please try again.');
    } finally {
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

 const handleBackToProjects = () => {
    navigate('/projects');
  };

return (
  <div className="main container py-4 mb-6">

    <div
      className="mx-auto"
      style={{
        maxWidth: "1000px",
        paddingLeft: "1rem",
        paddingRight: "1rem",
      }}
    >
      <div className="mb-3">
          <Button className="styled-button" onClick={handleBackToProjects}>
            Back to projects
          </Button>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Team management</h2>

        <Button className="styled-button" onClick={handleCreate} id="btn-create-user">
          Create user
        </Button>
      </div>

      <p className="mb-3">
        Manage the people who have access to the Our Journey application in your organisation.
      </p>

      {infoMessage && (
        <p style={{ color: "green", marginBottom: "1rem" }}>
          {infoMessage}
        </p>
      )}

      {loading && <p>Loading team members…</p>}

      {errorMessage && !loading && (
        <div className="form-error mb-3">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && (
        <>
          {users.length === 0 ? (
            <p>No team members found.</p>
          ) : (
            <div style={{ width: "100%", overflowX: "auto" }}>
              <UserTable
                users={users}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            </div>
          )}
        </>
      )}
    </div>

    <Dialog
      open={isDeleteDialogOpen}
      onClose={handleCloseDeleteDialog}
    >
      <DialogTitle>Delete user</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`Are you sure you want to delete ${
            userToDelete?.name ? `"${userToDelete.name}"` : "this user"
          }? This action cannot be undone.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="styled-button" onClick={handleCloseDeleteDialog}>
          Cancel
        </Button>
        <Button className="styled-button" onClick={handleConfirmDelete}>
          Yes, delete
        </Button>
      </DialogActions>
    </Dialog>
  </div>
);



}

export default TeamManagement;
